import ButtonBox from "component/ButtonBox/ButtonBox";
import DarkAndLightModeSwitch from "component/DarkAndLightModeSwitch/DarkAndLightModeSwitch";
import Loader from "component/Loader/Loader";
import Text from "component/Text";
import { AuthPageWelcomeHeader } from "component/sections/AuthPageWelcomeHeader/AuthPageWelcomeHeader";
import JoinTodayForm from "component/sections/JoinTodayForm/JoinTodayForm";
import JoinTodayViewController from "controller/JoinTodayController";
import { RouteProps, withRouter } from "hoc/withRouter";
import useDarkMode from "hook/useDarkMode";
import useIsWhitelabelled from "hook/useIsWhitelabelled";
import queryString from "query-string";
import { useEffect, useReducer } from "react";
import JoinTodayReducer, { INITIAL_JOINTODAY_STATE } from "reducer/auth/JoinTodayReducer";
import { linkPageLogin } from "routes";
import { ViewRenderStatus } from "util/CommonConstant";
import { attachRadialGradientListener } from "util/CommonUtil";

const PageJoinToday = (props: RouteProps) => {
  const [state, dispatch] = useReducer(JoinTodayReducer, {
    ...INITIAL_JOINTODAY_STATE,
  });

  const { productName } = useIsWhitelabelled();
  const isDarkMode = useDarkMode();

  const urlSearchQuery = queryString.parse(location.search, { decode: false });

  useEffect(() => {
    if (urlSearchQuery) {
      const email = urlSearchQuery.email as string;
      const invitationToken = urlSearchQuery.invitation_token as string;
      if (email && invitationToken) {
        init(email, invitationToken);
      } else {
        init("", "");
      }
    }
  }, []);

  // Create an instance of LoginController to manage form actions
  const joinController = new JoinTodayViewController(state, dispatch);

  useEffect(() => {
    const container = document.getElementById("container");
    const cleanup = attachRadialGradientListener(container, isDarkMode);
    return cleanup;
  }, [isDarkMode]);

  const init = (email: string, invitationToken: string) => {
    joinController.init(email, invitationToken);
  };

  const invalidLinkInfoContainer = () => {
    return (
      <div className="h-full w-full">
        <div
          className={`flex w-76.5 flex-col justify-center rounded-xl bg-light-grey-50 px-6 py-9 shadow-light-black-transparent-25-lg dark:bg-dark-neutral-gray-200 dark:shadow-light-black-transparent-25`}
        >
          <Text
            label={"label.text.joinToday.infoDialog.title"}
            className="pb-3 text-center text-lg font-semibold dark:text-dark-text-main"
          />

          <div className="w-full pb-6 text-center text-sm text-light-grey-900 dark:text-dark-neutral-gray-900">
            <Text label={"label.text.joinToday.infoDialog.description"} />
          </div>

          <div className="flex w-full flex-col items-center gap-2">
            <ButtonBox
              label={"label.text.joinToday.infoDialog.button"}
              onClick={() => props.navigateNoAuth(linkPageLogin)}
            />
          </div>
        </div>
      </div>
    );
  };

  const mainContainer = () => {
    if (state.data.isEmailOrTokenAbsent) {
      return invalidLinkInfoContainer();
    }

    return (
      <div className="flex flex-col items-center gap-6 p-6 xs:gap-x-16 xs:p-6 lg:flex-row">
        <AuthPageWelcomeHeader>
          <div className="text-body-copy-2 text-light-grey-600 dark:text-dark-text-v2 lg:w-111">
            <Text
              className="whitespace-pre"
              label={"label.text.joinToday.welcomeNote.line1"}
              args={{ productName }}
            />
            <br />
            <Text className="whitespace-pre" label={"label.text.joinToday.welcomeNote.line2"} />
            <br />
            <Text className="whitespace-pre" label={"label.text.joinToday.welcomeNote.line3"} />
          </div>
        </AuthPageWelcomeHeader>

        <div className="flex h-full w-full items-center justify-center sm:w-129">
          <JoinTodayForm formState={state} formController={joinController} />
        </div>
      </div>
    );
  };

  return (
    <div
      id="container"
      className="lg:min-w-screen flex min-h-screen w-full items-center justify-center bg-light-grey-100 text-primary-text-main dark:bg-light-patch-radial-gradient dark:text-dark-text-main"
    >
      {state.viewRenderStatus === ViewRenderStatus.LOADING ? (
        <Loader />
      ) : (
        <div className="lg:w-266 mx-auto">
          <div className="fixed right-6 top-6 z-10 max-w-fit">
            <DarkAndLightModeSwitch />
          </div>

          {mainContainer()}
        </div>
      )}
    </div>
  );
};

export default withRouter(PageJoinToday);
